import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../LoadingSpinner';

/**
 * SalesRoute Component
 * 
 * A route wrapper that allows access for both sales AND super_admin roles.
 * Used for bank parsing features that should be accessible to sales users.
 * If the user is not authenticated, they are redirected to the login page.
 * If the user is authenticated but doesn't have sales or super_admin role, 
 * they are redirected to the home page.
 * Shows a loading spinner while checking authentication status.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if user has access
 * @returns {JSX.Element} The sales content, a redirect to login, or a redirect to home
 */
const SalesRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Allow super_admin and sales roles
  if (user?.role !== 'sales' && user?.role !== 'super_admin') {
    return <Navigate to="/" />;
  }

  // Render children if authenticated and user has sales or super_admin role
  return children;
};

export default SalesRoute;

